import * as React from 'react';
import { OptionsPrediction } from '../../reducers/optionsSlice';

const formatPeriodDate = (dateStr: string) => {
    const date = new Date(`${dateStr}T12:00:00`);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPeriodRange = (startDate: string, endDate: string) => (
    `${formatPeriodDate(startDate)} – ${formatPeriodDate(endDate)}`
);

export interface PastStrategySummary {
    expirationDate: string;
    startDate: string;
    endDate: string;
    startPrice: number;
    endPrice: number;
    priceChangePercent: number;
    callVolume: number;
    putVolume: number;
    volumeRatio: number;
    callWins: number;
    putWins: number;
}

export interface FutureStrategySummary {
    currentPrice: number;
    startDate: string;
    endDate: string;
    pastVolumeStartDate?: string;
    pastVolumeEndDate?: string;
    prediction: OptionsPrediction;
    predictedCallStrikes: number;
    predictedPutStrikes: number;
    basedOnPastVolume?: boolean;
}

interface PastOptionsStrategyDashboardProps {
    summary?: PastStrategySummary | null;
    loading?: boolean;
}

interface NextOptionsStrategyDashboardProps {
    summary: FutureStrategySummary;
}

export const PastOptionsStrategyDashboard: React.FC<PastOptionsStrategyDashboardProps> = ({
    summary = null,
    loading = false,
}) => {
    if (!summary && !loading) {
        return null;
    }

    return (
        <div className="options-strategy-dashboard past">
            {loading && !summary && (
                <div className="panel-loading">Loading past strategy data...</div>
            )}

            {summary && (
                <div className="metrics-grid">
                    <div className="metric-card">
                        <div className="metric-label">Period</div>
                        <div className="metric-value metric-value-sm">{formatPeriodRange(summary.startDate, summary.endDate)}</div>
                        <div className="metric-subtext">Volume window · expiry {summary.expirationDate}</div>
                    </div>
                    <div className="metric-card">
                        <div className="metric-label">Price at Expiry</div>
                        <div className="metric-value">${summary.endPrice.toFixed(2)}</div>
                        <div className={`metric-subtext ${summary.priceChangePercent >= 0 ? 'positive' : 'negative'}`}>
                            {summary.priceChangePercent >= 0 ? '+' : ''}{summary.priceChangePercent.toFixed(2)}% from ${summary.startPrice.toFixed(2)}
                        </div>
                    </div>
                    <div className="metric-card">
                        <div className="metric-label">Call Wins</div>
                        <div className="metric-value success">{summary.callWins}</div>
                        <div className="metric-subtext">ITM at expiry</div>
                    </div>
                    <div className="metric-card">
                        <div className="metric-label">Put Wins</div>
                        <div className="metric-value success">{summary.putWins}</div>
                        <div className="metric-subtext">ITM at expiry</div>
                    </div>
                    <div className="metric-card">
                        <div className="metric-label">Call Volume</div>
                        <div className="metric-value call-color">{summary.callVolume.toLocaleString()}</div>
                    </div>
                    <div className="metric-card">
                        <div className="metric-label">Put Volume</div>
                        <div className="metric-value put-color">{summary.putVolume.toLocaleString()}</div>
                    </div>
                    <div className="metric-card">
                        <div className="metric-label">Put/Call Ratio</div>
                        <div className="metric-value">{summary.volumeRatio.toFixed(2)}x</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export const NextOptionsStrategyDashboard: React.FC<NextOptionsStrategyDashboardProps> = ({ summary }) => {
    const pastVolumeRange = summary.pastVolumeStartDate && summary.pastVolumeEndDate
        ? formatPeriodRange(summary.pastVolumeStartDate, summary.pastVolumeEndDate)
        : null;

    return (
        <div className="options-strategy-dashboard next">
            <div className="metrics-grid">
                <div className="metric-card">
                    <div className="metric-label">Forward Period</div>
                    <div className="metric-value metric-value-sm">{formatPeriodRange(summary.startDate, summary.endDate)}</div>
                </div>
                {pastVolumeRange && (
                    <div className="metric-card">
                        <div className="metric-label">Past Volume Basis</div>
                        <div className="metric-value metric-value-sm">{pastVolumeRange}</div>
                        <div className="metric-subtext">Aggregated call/put volume</div>
                    </div>
                )}
                <div className="metric-card">
                    <div className="metric-label">Signal</div>
                    <div className={`metric-value metric-value-sm signal-${summary.prediction.prediction}`}>
                        {summary.prediction.prediction}
                    </div>
                    <div className="metric-subtext">From past 3M put/call ratio</div>
                </div>
                <div className="metric-card">
                    <div className="metric-label">Current Price</div>
                    <div className="metric-value">${summary.currentPrice.toFixed(2)}</div>
                </div>
                <div className="metric-card">
                    <div className="metric-label">Confidence</div>
                    <div className="metric-value">{summary.prediction.confidence.toFixed(0)}%</div>
                    <div className="metric-subtext">Past volume signal</div>
                </div>
                <div className="metric-card">
                    <div className="metric-label">Predicted Calls</div>
                    <div className="metric-value predicted">{summary.predictedCallStrikes}</div>
                    <div className="metric-subtext">Past 3M high-volume strikes</div>
                </div>
                <div className="metric-card">
                    <div className="metric-label">Predicted Puts</div>
                    <div className="metric-value predicted">{summary.predictedPutStrikes}</div>
                    <div className="metric-subtext">Past 3M high-volume strikes</div>
                </div>
                <div className="metric-card">
                    <div className="metric-label">Past Call Volume</div>
                    <div className="metric-value call-color">{summary.prediction.callVolume.toLocaleString()}</div>
                </div>
                <div className="metric-card">
                    <div className="metric-label">Past Put Volume</div>
                    <div className="metric-value put-color">{summary.prediction.putVolume.toLocaleString()}</div>
                </div>
                <div className="metric-card">
                    <div className="metric-label">Put/Call Ratio</div>
                    <div className="metric-value">{summary.prediction.volumeRatio.toFixed(2)}x</div>
                    <div className="metric-subtext">Past 3 months aggregate</div>
                </div>
            </div>
        </div>
    );
};
